import React, { useEffect, useState } from 'react';
import { Mic, Bot, AlertTriangle, Play, Search, X } from 'lucide-react';
import { adminApi, mediaUrl, fmtMinutes } from '../../lib/programsApi';

export const SRC = {
  tts: { label: 'Rowdy Voice TTS', emoji: '🤖', cls: 'bg-amber-50 text-amber-700 border-amber-200', Icon: Bot },
  recording: { label: 'Rowdy Recording', emoji: '🎙', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: Mic },
  missing: { label: 'Missing Audio', emoji: '⚠', cls: 'bg-rose-50 text-rose-700 border-rose-200', Icon: AlertTriangle },
};

export const AudioBadge = ({ source, testId }) => {
  const s = SRC[source] || SRC.missing;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${s.cls}`} data-testid={testId}>
      {s.emoji} {s.label}
    </span>
  );
};

export const InlinePreview = ({ url, testId }) => (
  url ? <audio controls preload="none" src={mediaUrl(url)} className="h-8 w-full max-w-[280px]" data-testid={testId} /> : <span className="text-[11px] text-stone-400">No audio</span>
);

export const ContentPickerModal = ({ open, onClose, onPick, types, title }) => {
  const [q, setQ] = useState('');
  const [type, setType] = useState(types || '');
  const [src, setSrc] = useState('');
  const [status, setStatus] = useState('');
  const [items, setItems] = useState([]);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      adminApi.search({ q, type, audioSource: src, status, limit: 60 }).then((d) => setItems(d.items || [])).catch(() => setItems([]));
    }, 200);
    return () => clearTimeout(t);
  }, [open, q, type, src, status]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[300] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="content-picker-modal">
      <div className="bg-white w-full sm:max-w-2xl max-h-[90vh] rounded-t-3xl sm:rounded-3xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-stone-200 flex items-center gap-2">
          <h3 className="text-sm font-semibold text-stone-800 flex-1">{title || 'Choose canonical content'}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-stone-100 flex items-center justify-center" data-testid="content-picker-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-3 grid grid-cols-2 sm:grid-cols-4 gap-2 border-b border-stone-100">
          <label className="col-span-2 sm:col-span-1 flex items-center gap-1.5 border border-stone-200 rounded-lg px-2 h-9 text-sm">
            <Search className="w-3.5 h-3.5 text-stone-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title…" className="flex-1 outline-none text-sm text-stone-800" data-testid="content-picker-search" />
          </label>
          <select value={type} onChange={(e) => setType(e.target.value)} className="h-9 border border-stone-200 rounded-lg px-2 text-sm text-stone-800" data-testid="content-picker-type">
            <option value="">All types</option><option value="meditation">Meditation</option><option value="sleep_meditation">Sleep meditation</option>
            <option value="soundscape">Soundscape</option><option value="video">Video</option><option value="affirmation">Affirmation</option>
          </select>
          <select value={src} onChange={(e) => setSrc(e.target.value)} className="h-9 border border-stone-200 rounded-lg px-2 text-sm text-stone-800" data-testid="content-picker-source">
            <option value="">Any audio source</option><option value="recording">🎙 Rowdy Recording</option><option value="tts">🤖 Rowdy Voice TTS</option><option value="missing">⚠ Missing Audio</option>
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 border border-stone-200 rounded-lg px-2 text-sm text-stone-800" data-testid="content-picker-status">
            <option value="">Any status</option><option value="published">Published</option><option value="draft">Draft</option><option value="scheduled">Scheduled</option>
          </select>
        </div>
        <div className="overflow-y-auto p-3 space-y-2 flex-1">
          {items.length === 0 && <p className="text-sm text-stone-500 text-center py-6">No matching content.</p>}
          {items.map((it) => (
            <div key={it.id} className="border border-stone-200 rounded-xl p-3 flex items-start gap-3" data-testid={`content-picker-item-${it.slug || it.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-stone-800 truncate">{it.title}</p>
                <p className="text-[11px] text-stone-500">{it.type} · {fmtMinutes(it.durationSeconds) || '—'} · {it.status}</p>
                <div className="mt-1 flex items-center gap-2 flex-wrap">
                  <AudioBadge source={it.audioSource} />
                  {it.audioUrl && (
                    <button type="button" onClick={() => setPreview(preview === it.id ? null : it.id)} className="text-[11px] font-semibold text-[#C79A4A] inline-flex items-center gap-1" data-testid={`content-picker-preview-${it.slug || it.id}`}>
                      <Play className="w-3 h-3" /> Preview
                    </button>
                  )}
                </div>
                {preview === it.id && <div className="mt-2"><InlinePreview url={it.audioUrl} /></div>}
              </div>
              <button type="button" onClick={() => onPick(it)} className="px-3 h-9 rounded-full bg-[#173F35] text-white text-xs font-semibold" data-testid={`content-picker-select-${it.slug || it.id}`}>Select</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
